<script setup lang="ts">
import { onMounted, ref, computed, shallowRef, watchEffect } from 'vue';

import {
  CabinTypeLabel,
  EngineTypeLabel,
  DrivetrainLabel,
  RoofTypeLabel,
  SizeTypeLabel,
  TechDataVariations,
  TechDataVehicle,
} from './types';
import { getPageEnvironmentVariables } from './utils/aem';

import TechDataSelector from '@/components/tech-data-selector.vue';
import TechDataSlider from './components/tech-data-slider.vue';
import TechDataTable from './components/tech-data-table.vue';
import { loadTranslations } from './service/i18n';

import { useTracking } from '@/composables/useTracking';
import { useIntersectionObserverOnce } from '@/composables/useIntersectionObserverOnce';
import { useFilters } from './composables/useFiltersOptions';

type TransmissionMode = 'manual' | 'automatic';
const props = defineProps<{
  data: {
    aemData: {
      modelSeries: string;
      stage: string;
      language: string;
      country: string;
      title: string;
    };
    processedData: {
      sizes: SizeTypeLabel[];
      roofs: RoofTypeLabel[];
      cabins: CabinTypeLabel[];
      batteryCapacities: number[];
      engines: EngineTypeLabel[];
      weights: number[];
      drivetrains: DrivetrainLabel[];
      vehicles: TechDataVehicle[];
      variations: TechDataVariations;
    };
    translations: Record<string, string>;
    selectedVariant: TechDataVehicle;
  };
}>();

const country = ref('');
const language = ref('');
const stage = ref('');
const modelSeries = ref('');
const title = ref('');
const locale = ref('');
const sizes = ref<SizeTypeLabel[]>([]);
const roofs = ref<RoofTypeLabel[]>([]);
const cabins = ref<CabinTypeLabel[]>([]);
const engines = ref<EngineTypeLabel[]>([]);
const batteryCapacities = ref<number[]>([]);
const weights = ref<number[]>([]);
const drivetrains = ref<DrivetrainLabel[]>([]);
const variations = ref<TechDataVariations>();
const vehicles = ref<TechDataVehicle[]>([]);
const selected_variant = shallowRef<TechDataVehicle | undefined>(
  props.data?.selectedVariant,
);

const translations = ref<Record<string, string>>({});
const appContainer = ref<HTMLElement | null>(null);
const hasTrackedImpression = ref(false);

function getDefaultTransmission(
  v: TechDataVehicle | undefined,
): TransmissionMode {
  if (!v) return 'manual';

  const list = (v.transmissions ?? [])
    .map((t) => String(t).toLowerCase())
    .filter((t): t is TransmissionMode => t === 'manual' || t === 'automatic');

  if (list.includes('manual')) return 'manual';
  if (list.includes('automatic')) return 'automatic';

  if (v.emissionsConsumption?.manual) return 'manual';
  if (v.emissionsConsumption?.automatic) return 'automatic';

  return 'manual';
}

modelSeries.value = props.data?.aemData.modelSeries || '';
stage.value = props.data?.aemData.stage || '';
language.value = props.data?.aemData.language || '';
country.value = props.data?.aemData.country || '';
locale.value = `${props.data?.aemData.language}-${props.data?.aemData.country}`;
translations.value = props.data?.translations || {};
selected_variant.value = props.data?.selectedVariant;
title.value = props.data?.aemData.title;

const { trackImpression } = useTracking(modelSeries.value);

onMounted(async () => {
  const envData = await getPageEnvironmentVariables();

  if (!Object.keys(translations.value).length) {
    try {
      const data = await loadTranslations(envData.language, envData.country);
      translations.value = data ?? {};
    } catch (error) {
      console.error('Failed to load translations:', error);
      translations.value = {};
    }
  }

  if (!props.data.aemData) {
    modelSeries.value = envData.modelSeries;
    stage.value = envData.stage;
    language.value = envData.language;
    country.value = envData.country;
    locale.value = envData.locale;
  }

  if (props.data.selectedVariant) {
    selected_variant.value = props.data.selectedVariant;
  }
});

sizes.value = props.data?.processedData.sizes ?? [];
roofs.value = props.data?.processedData.roofs ?? [];
cabins.value = props.data?.processedData.cabins ?? [];
engines.value = props.data?.processedData.engines ?? [];
batteryCapacities.value = props.data?.processedData.batteryCapacities ?? [];
weights.value = props.data?.processedData.weights ?? [];
drivetrains.value = props.data?.processedData.drivetrains ?? [];
vehicles.value = props.data?.processedData.vehicles ?? [];
variations.value = props.data?.processedData.variations;

useIntersectionObserverOnce(appContainer, { threshold: [0.25] }, () => {
  if (!hasTrackedImpression.value && vehicles.value.length > 0) {
    trackImpression(vehicles.value[0]);
    hasTrackedImpression.value = true; // Set flag after first fire
  }
});

const transmission_override = shallowRef<TransmissionMode | null>(null);
const effective_transmission = computed<TransmissionMode | null>(() => {
  if (transmission_override.value !== null) return transmission_override.value;
  if (!selected_variant.value) return null;
  return getDefaultTransmission(selected_variant.value);
});

const updateSelectedVariant = (e: TechDataVehicle) => {
  selected_variant.value = e;
  transmission_override.value = null;
};

const updateSelectedTransmission = (m: TransmissionMode) => {
  transmission_override.value = m;
};

const filteredVehicles = computed(() => {
  if (!selected_variant.value) return [];
  return vehicles.value.filter(
    (v) => v.sizeType === selected_variant.value?.sizeType,
  );
});

const { validOptions } = useFilters({
  weights: weights.value,
  roofs: roofs.value,
  cabins: cabins.value,
  drivetrains: drivetrains.value,
  batteryCapacities: batteryCapacities.value,
  engines: engines.value,
});

defineExpose({
  variations,
  vehicles,
  language,
  locale,
  modelSeries,
});
</script>

<template>
  <div id="owvans-app" class="owvans-tech-data" dir="ltr" ref="appContainer">
    <div v-if="variations?.variations.length && vehicles.length">
      <span v-if="title" class="owvans-tech-data__title wb-heading-l">{{
        title
      }}</span>
      <div class="wb-grid-container">
        <tech-data-selector
          :sizes="sizes"
          :validOptions="validOptions"
          :variations="variations"
          :vehicles="vehicles"
          :translations="translations"
          :selectedVariant="selected_variant"
          :modelSeries="modelSeries"
          class="owvans-tech-data__selector"
          @variant-selected="updateSelectedVariant($event)"
          @selected-transmission="updateSelectedTransmission($event)"
        />
      </div>
      <tech-data-slider
        v-if="selected_variant"
        :images="selected_variant.imagesData"
        :modelSeries="modelSeries"
        :translations="translations"
        :vehicle="selected_variant"
        class="owvans-tech-data-wrapper__slider"
      />
      <tech-data-table
        v-if="selected_variant"
        :validOptions="validOptions"
        :translations="translations"
        :vehicle="selected_variant"
        :vehicles="filteredVehicles"
        :locale="locale"
        :modelSeries="modelSeries"
        :transmissionMode="effective_transmission"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import url('https://assets.oneweb.mercedes-benz.com/plugin/workbench/core/7.81.0/css/globals.css');
@import '@workbench/core/dist/scss/utility.scss';
</style>
